import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Button, EmptyState, Notice, PageHeader, RowActions, StatusIndicator, Table, Tag, type IndicatorState } from '@voice/ui';
import { errorText, formatDateTime, formatDuration } from '../components/common';
import { api } from '../lib/api';
import styles from './Pages.module.css';
import wb from './Wallboard.module.css';

interface Agent {
  extensionId: string;
  number: string;
  name: string;
  state: 'loggedout' | 'paused' | 'dnd' | 'offline' | 'ringing' | 'busy' | 'wrapup' | 'available';
  pauseReason: string | null;
  penalty: number;
  callsToday: number;
}
interface QueueLive {
  id: string;
  name: string;
  number: string | null;
  strategy: string;
  waiting: { callId: string; position: number; number: string; name: string | null; waitingMs: number; ringing: boolean }[];
  longestWaitMs: number;
  agents: Agent[];
  stats: { answered: number; abandoned: number; overflowed: number; avgWaitMs: number; serviceLevel: number | null; serviceLevelSeconds: number };
}
interface LiveResponse {
  queues: QueueLive[];
  callbacks: { id: string; queueId: string; number: string; name: string | null; requestedAt: string; attempts: number }[];
}

const AGENT_STATE: Record<Agent['state'], { label: string; tone: IndicatorState }> = {
  available: { label: 'Frei', tone: 'ok' },
  ringing: { label: 'Klingelt', tone: 'ringing' },
  busy: { label: 'Im Gespräch', tone: 'busy' },
  wrapup: { label: 'Nachbearbeitung', tone: 'busy' },
  paused: { label: 'Pause', tone: 'off' },
  dnd: { label: 'Nicht stören', tone: 'off' },
  offline: { label: 'Nicht erreichbar', tone: 'off' },
  loggedout: { label: 'Abgemeldet', tone: 'off' },
};

function useTick(ms = 1000): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

export function Wallboard() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['queues-live'], queryFn: () => api<LiveResponse>('/queues/live'), refetchInterval: 15_000 });
  const now = useTick();
  // Die Wartezeit läuft aus einem festen Startzeitpunkt je Anrufer – nicht aus jeder neuen Abfrage neu, sonst „zuckt“ der Zähler.
  const starts = useRef(new Map<string, number>());
  const waited = (callId: string, waitingMs: number): number => {
    const estimate = query.dataUpdatedAt - waitingMs;
    const known = starts.current.get(callId);
    if (known === undefined || Math.abs(estimate - known) > 3000) starts.current.set(callId, estimate);
    return Math.max(0, now - starts.current.get(callId)!);
  };
  const refresh = () => qc.invalidateQueries({ queryKey: ['queues-live'] });
  const agent = useMutation({ mutationFn: (v: { queueId: string; extensionId: string; loggedIn?: boolean; paused?: boolean }) => api(`/queues/${v.queueId}/agents/${v.extensionId}`, { method: 'POST', body: { loggedIn: v.loggedIn, paused: v.paused } }), onSuccess: refresh });
  const take = useMutation({ mutationFn: (callId: string) => api(`/queues/calls/${callId}/take`, { method: 'POST' }), onSuccess: refresh });
  const dropCallback = useMutation({ mutationFn: (id: string) => api(`/callbacks/${id}`, { method: 'DELETE' }), onSuccess: refresh });

  return (
    <div className={styles.page} style={{ maxWidth: 1300 }}>
      <PageHeader title="Live-Ansicht Warteschlangen" />
      {query.isPending && <p className={styles.note}>Lädt …</p>}
      {query.isError && <Notice tone="error">Die Live-Daten konnten nicht geladen werden.</Notice>}
      {query.data?.queues.length === 0 && <EmptyState>Keine Warteschlangen eingerichtet.</EmptyState>}
      {agent.isError && <Notice tone="error">{errorText(agent.error)}</Notice>}
      {take.isError && <Notice tone="error">{errorText(take.error)}</Notice>}
      {take.isSuccess && <Notice tone="success">Deine Nebenstelle klingelt gleich – nach dem Abheben bist du mit dem Anrufer verbunden. (Läuft gerade noch ein Klingeln bei einem anderen Mitarbeiter, geht es danach sofort los.)</Notice>}

      <div className={wb.grid}>
        {query.data?.queues.map((q) => {
          const callbacks = query.data.callbacks.filter((c) => c.queueId === q.id);
          const free = q.agents.filter((a) => a.state === 'available').length;
          const loggedIn = q.agents.filter((a) => a.state !== 'loggedout').length;
          return (
            <section key={q.id} className={wb.card} aria-label={q.name}>
              <header className={wb.head}>
                <h2>{q.name}</h2>
                {q.number && <span className={styles.mono}>{q.number}</span>}
              </header>

              <dl className={wb.stats}>
                <div>
                  <dt>Wartend</dt>
                  <dd className={q.waiting.length > 0 ? wb.alert : ''}>{q.waiting.length}</dd>
                </div>
                <div>
                  <dt>Längste Wartezeit</dt>
                  <dd>{q.waiting.length ? formatDuration(Math.max(...q.waiting.map((w) => waited(w.callId, w.waitingMs)))) : '–'}</dd>
                </div>
                <div>
                  <dt>Frei / angemeldet</dt>
                  <dd>
                    {free} / {loggedIn}
                  </dd>
                </div>
                <div>
                  <dt>Angenommen</dt>
                  <dd>{q.stats.answered}</dd>
                </div>
                <div>
                  <dt>Aufgelegt</dt>
                  <dd>{q.stats.abandoned}</dd>
                </div>
                <div>
                  <dt>Service-Level ({q.stats.serviceLevelSeconds} s)</dt>
                  <dd>{q.stats.serviceLevel === null ? '–' : `${q.stats.serviceLevel} %`}</dd>
                </div>
              </dl>

              {q.waiting.length > 0 && (
                <Table aria-label={`Wartende in ${q.name}`}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Anrufer</th>
                      <th>Wartet</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {q.waiting.map((w) => (
                      <tr key={w.callId}>
                        <td>{w.position}</td>
                        <td>
                          {w.name ?? w.number}
                          {w.ringing && <Tag tone="ok">klingelt</Tag>}
                        </td>
                        <td>{formatDuration(waited(w.callId, w.waitingMs))}</td>
                        <td>
                          <RowActions>
                            <Button variant="secondary" onClick={() => take.mutate(w.callId)} disabled={take.isPending}>
                              Übernehmen
                            </Button>
                          </RowActions>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              <Table aria-label={`Mitarbeiter in ${q.name}`}>
                <thead>
                  <tr>
                    <th>Mitarbeiter</th>
                    <th>Zustand</th>
                    <th>Heute</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {q.agents.map((a) => {
                    const s = AGENT_STATE[a.state];
                    return (
                      <tr key={a.extensionId}>
                        <th scope="row">
                          {a.number} <span className={styles.muted}>{a.name}</span>
                        </th>
                        <td>
                          <StatusIndicator state={s.tone} label={a.state === 'paused' && a.pauseReason ? `Pause (${a.pauseReason})` : s.label} />
                        </td>
                        <td>{a.callsToday}</td>
                        <td>
                          <RowActions>
                            <Button variant="secondary" onClick={() => agent.mutate({ queueId: q.id, extensionId: a.extensionId, loggedIn: a.state === 'loggedout' })}>
                              {a.state === 'loggedout' ? 'Anmelden' : 'Abmelden'}
                            </Button>
                            {a.state !== 'loggedout' && (
                              <Button variant="secondary" onClick={() => agent.mutate({ queueId: q.id, extensionId: a.extensionId, paused: a.state !== 'paused' })}>
                                {a.state === 'paused' ? 'Pause beenden' : 'Pause'}
                              </Button>
                            )}
                          </RowActions>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              {callbacks.length > 0 && (
                <div>
                  <h3 className={styles.heading}>Offene Rückrufe</h3>
                  <div className={styles.deviceList}>
                    {callbacks.map((c) => (
                      <div key={c.id} className={styles.device}>
                        <div className={styles.deviceInfo}>
                          <span className={styles.deviceName}>{c.name ?? c.number}</span>
                          <span className={styles.muted}>
                            {c.number} · seit {formatDateTime(c.requestedAt)} · {c.attempts} Versuch{c.attempts === 1 ? '' : 'e'}
                          </span>
                        </div>
                        <Button variant="secondary" onClick={() => dropCallback.mutate(c.id)}>
                          Verwerfen
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
